/**
 * One Script control: speech packs and IVR graphs in a single list.
 * Backend still stores pack + ivr_id; never pick both.
 */

import type { PackInfo } from "./types";

export type ScriptKind = "pack" | "ivr";

export interface ScriptOption {
  kind: ScriptKind;
  slug: string;
  label: string;
  value: string;
}

export const DEFAULT_SCRIPT_VALUE = "pack:default";

export function scriptOptionValue(kind: ScriptKind, slug: string): string {
  const token = slug.trim() || (kind === "pack" ? "default" : "");
  return `${kind}:${token}`;
}

export function parseScriptValue(value: string): { kind: ScriptKind; slug: string } {
  const raw = value.trim();
  if (raw.startsWith("ivr:")) {
    return { kind: "ivr", slug: raw.slice(4).trim() };
  }
  if (raw.startsWith("pack:")) {
    return { kind: "pack", slug: raw.slice(5).trim() || "default" };
  }
  return { kind: "pack", slug: raw || "default" };
}

/** Map stored pack + ivr_id onto the single Script select value. */
export function formToScriptValue(pack: string, ivrId: string): string {
  const ivr = ivrId.trim();
  if (ivr) return scriptOptionValue("ivr", ivr);
  return scriptOptionValue("pack", pack.trim() || "default");
}

/** Choosing a pack clears ivr_id. Choosing a graph sets pack=default. */
export function scriptValueToForm(value: string): { pack: string; ivr_id: string } {
  const { kind, slug } = parseScriptValue(value);
  if (kind === "ivr" && slug) {
    return { pack: "default", ivr_id: slug };
  }
  return { pack: slug || "default", ivr_id: "" };
}

export interface IvrListItem {
  slug: string;
  name?: string;
  label?: string;
}

/**
 * Packs first, then IVR graphs. Slug `office` is listed once if it is both.
 */
export function mergeScriptOptions(packs: PackInfo[], ivrs: IvrListItem[]): ScriptOption[] {
  const options: ScriptOption[] = [];
  const seenOffice = { pack: false, ivr: false };

  for (const item of packs) {
    const slug = String(item.slug ?? "").trim();
    if (!slug) continue;
    if (slug === "office") {
      if (seenOffice.pack || seenOffice.ivr) continue;
      seenOffice.pack = true;
    }
    options.push({
      kind: "pack",
      slug,
      label: String(item.label ?? slug),
      value: scriptOptionValue("pack", slug),
    });
  }

  for (const item of ivrs) {
    const slug = String(item.slug ?? "").trim();
    if (!slug) continue;
    if (slug === "office") {
      if (seenOffice.pack || seenOffice.ivr) continue;
      seenOffice.ivr = true;
    }
    options.push({
      kind: "ivr",
      slug,
      label: String(item.name ?? item.label ?? slug),
      value: scriptOptionValue("ivr", slug),
    });
  }

  return options;
}

/** Map a campaign script_slug onto a Script option (pack or graph). */
export function campaignSlugToScriptValue(slug: string, options: ScriptOption[]): string {
  const key = slug.trim() || "office";
  const ivr = options.find((item) => item.kind === "ivr" && item.slug === key);
  if (ivr) return ivr.value;
  const pack = options.find((item) => item.kind === "pack" && item.slug === key);
  if (pack) return pack.value;
  if (key === "office") {
    const fallback = options.find((item) => item.kind === "pack" && item.slug === "default");
    if (fallback) return fallback.value;
  }
  return scriptOptionValue("pack", key === "office" ? "default" : key);
}
